import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MultiSelectField } from '@/components/atoms/MultiSelect';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

import type { CampaignSetupForm } from '../types';

interface TagOption {
  label: string;
  value: string;
  disabled?: boolean;
}

interface TagsFieldProps {
  form: UseFormReturn<CampaignSetupForm>;
  canWrite: boolean;
  allTagOptions: TagOption[];
  onTagsChange: (selectedTags: TagOption[]) => void;
  onCreateNewTag: (newTagValue: string) => void;
}

export const TagsField = ({
  form,
  canWrite,
  allTagOptions,
  onTagsChange,
  onCreateNewTag,
}: TagsFieldProps) => {
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name="tags"
      render={({ field }) => (
        <FormItem className="mt-6">
          <FormLabel>{t('campaigns.setup.tags')}</FormLabel>
          <FormControl>
            <MultiSelectField
              title={t('analytics.fields.tag')}
              options={allTagOptions}
              value={field.value?.map((tag) => tag.value) || []}
              onChange={(selectedValues) => {
                const selectedTags = selectedValues
                  .map((value) => allTagOptions.find((option) => option.value === value))
                  .filter(Boolean) as TagOption[];
                field.onChange(selectedTags);
                onTagsChange(selectedTags);
              }}
              placeholder={t('campaigns.setup.tags_search_placeholder')}
              onCreateNew={onCreateNewTag}
              createNewText={t('campaigns.setup.tags_create_new')}
              noResultsText={t('campaigns.setup.tags_no_results')}
              disabled={!canWrite}
            />
          </FormControl>
          <FormDescription>{t('campaigns.setup.tags_description')}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
